import { memo, useMemo, type CSSProperties } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store';
import { hexA, nodeTint, ROOT_GRADIENT } from '../../lib/color';
import { HEAT_HEX, type HeatBucket } from '../../lib/health';
import { DocPill } from '../../components/DocPill';
import { Avatar } from '../../components/Avatar';
import type { NodeId } from '../../lib/types';
import { buildNodeView, META_SEP, type BeanRow, type NodeView } from './nodeView';
import { countNodeRender } from './renderCount';
import { NodeRename } from './NodeRename';

/**
 * One node on the map.
 *
 * THE MARKUP IS `nodeEl()`'S, ELEMENT FOR ELEMENT. Same classes, same child order, same
 * inline styles, same `data-*` markers — because every pixel baseline in `e2e/__screenshots__`
 * has to pass unedited, and because the pointer handling in `MapStage` still delegates
 * through `closest('[data-toggle]')` and friends exactly as the original did. What changed is
 * that the markup is described rather than built: the original assembled 147 elements with
 * `createElement` on every one of ~50 render calls and threw them all away each time.
 *
 * THE SUBSCRIPTION IS THE POINT. `useShallow(buildNodeView)` re-renders this node when — and
 * only when — one of the values it draws changes. A pan changes none of them (the camera is a
 * transform on the world, applied without a render at all), so a pan re-renders zero nodes; a
 * label edit changes exactly one node's `label`, so it re-renders exactly one.
 *
 * `memo` is not redundant beside that subscription: it stops the parent's re-render — which
 * happens on every vanilla `render()` — from reaching down here at all. Between the two, the
 * only thing that can re-render a node is a change to what the node itself shows.
 */

export interface MapNodeProps {
  id: NodeId;
}

function MapNodeInner({ id }: MapNodeProps) {
  const v = useAppStore(useShallow((s) => buildNodeView(s, id)));
  countNodeRender(id);

  // Value-compared lists, decoded. See the note in `nodeView.ts` on why they travel encoded.
  const beans = useMemo(() => JSON.parse(v.beansJson) as BeanRow[], [v.beansJson]);
  const metaColors = useMemo(
    () => (v.metaColors ? v.metaColors.split(META_SEP) : []),
    [v.metaColors],
  );
  const badges = useMemo(() => (v.presenceJson ? (JSON.parse(v.presenceJson) as BadgeRow[]) : []), [
    v.presenceJson,
  ]);

  if (!v.present) return null;

  const classes = ['node'];
  if (v.root) classes.push('root');
  if (v.tagHit) classes.push('tag-hit');
  if (v.tagDim) classes.push('tag-dim');
  if (v.searchHit) classes.push('search-hit');
  if (v.searchDim) classes.push('search-dim');
  if (v.statusHit) classes.push('docstatus-hit');
  if (v.statusDim) classes.push('docstatus-dim');
  if (v.chatHit) classes.push('chat-hit');
  if (v.chatDim) classes.push('chat-dim');
  if (v.assignHit) classes.push('assign-hit');
  if (v.assignDim) classes.push('assign-dim');
  if (v.dragging) classes.push('dragging');
  if (v.dropTarget) classes.push('drop-target');
  // The node is somewhere else — in a popup's seat, a page's bubble, or in the air between
  // them (V0.47). Its box stays laid out; only its paint goes, so the map never reflows.
  if (v.away) classes.push('away');
  if (v.linkSel) classes.push('linksel');
  // A project node (V0.50.3): the flag inside the box, and — heatmap mode, for a reader who
  // may see the to-dos — the progress underline, which needs a little more room below the label.
  if (v.project) classes.push('project');
  if (v.projectTotal >= 0) classes.push('pj-bar');

  const style = styleFor(v);

  return (
    // `title` exists only while the node carries heat — with the mode off the attribute is
    // absent, so the mode-off markup stays byte-identical to before the heatmap existed.
    <div className={classes.join(' ')} data-id={id} style={style} title={v.heatTitle || undefined}>
      {v.root ? null : <span className="swatch" style={{ background: paintOf(v) }} />}
      {/* THE PROJECT MARK (V0.50.3): a flag between the swatch and the label, so the node reads
          "project" before its name. INSIDE the box — every corner is already spoken for (the
          clearance badge, presence, the meta dots, the doc pills). One glyph, `#i-flag`, the
          same one the popup's head and the dashboard tab carry. */}
      {v.project ? (
        <svg className="pj-mark" viewBox="0 0 24 24" aria-hidden="true" data-project="1">
          <use href="#i-flag" />
        </svg>
      ) : null}
      {/* The clearance state, stamped as an ICON so it survives colour-blindness, a projector,
          and a node someone coloured to match a bucket by hand (V0.42). Slate carries `''` and
          shows nothing — an exemption needs no alarm. The chip colour IS the bucket colour;
          `data-heat` is what the spec keys on. */}
      {v.heatIcon ? (
        <svg
          className="heat-badge"
          data-heat={v.heat}
          viewBox="0 0 24 24"
          aria-hidden="true"
          style={{ color: HEAT_HEX[v.heat as HeatBucket] }}
        >
          <use href={`#${v.heatIcon}`} />
        </svg>
      ) : null}
      {/* The rename input REPLACES the label, as `startRename()` did with `replaceWith`. */}
      <NodeRename id={id} label={v.label} />
      {!v.root && v.hasChildren ? (
        <button
          className={v.side < 0 ? 'toggle left' : 'toggle'}
          data-toggle={id}
          style={v.rawColor ? { borderColor: hexA(v.rawColor, 0.6), color: v.rawColor } : undefined}
        >
          {v.collapsed ? '+' : '–'}
        </button>
      ) : null}
      {/* Deletion is EDIT_MAP's (V0.37): without the brick the cross is absent, not merely
          inert — the pointer handler would refuse the click, and a dead control is worse. */}
      {v.root || !v.mapEditable ? null : (
        <svg className="close" viewBox="0 0 24 24" data-del={id}>
          <use href="#i-x" />
        </svg>
      )}
      {v.hasDesc || metaColors.length ? (
        <span className="meta">
          {v.hasDesc ? <span className="m" /> : null}
          {metaColors.map((c, i) => (
            <span className="m" key={i} style={{ background: c }} />
          ))}
        </span>
      ) : null}
      {beans.length || (!v.expandedRows && v.customCount) ? (
        <span className={v.expandedRows ? 'docbeans expanded' : 'docbeans'}>
          {beans.map((b, i) => (
            <Bean key={b.docId ?? `more-${i}`} bean={b} expanded={v.expandedRows} />
          ))}
          {/* Zoomed out, every custom document collapses into one "+N" count badge. */}
          {!v.expandedRows && v.customCount ? (
            <span
              className="bean bean-count"
              data-opendoc-more="1"
              style={{ borderColor: 'color-mix(in srgb, var(--ink-faint) 70%, black)' }}
              title={`${v.customCount} custom document${v.customCount > 1 ? 's' : ''}`}
            >
              +{v.customCount}
            </span>
          ) : null}
        </span>
      ) : null}
      {/* HEATMAP MODE ONLY (V0.50.3): the subtree's progress as a thin bar along the inside of
          the bottom edge — the mode that already paints every node by its state is where a
          project's state belongs too. Figures are this reader's own rollup; without VIEW_TODOS
          there are none and the mark stands alone. */}
      {v.projectTotal >= 0 ? (
        <span
          className="pj-under"
          title={`${v.projectDone} of ${v.projectTotal} tasks done`}
          data-done={v.projectDone}
          data-total={v.projectTotal}
        >
          <i style={{ width: `${v.projectTotal ? Math.round((v.projectDone / v.projectTotal) * 100) : 0}%` }} />
        </span>
      ) : null}
      {badges.length ? (
        <div className="node-presence">
          {badges.slice(0, 3).map((b) => (
            <Avatar
              key={b.userId}
              user={{ userId: b.userId, initials: b.initials, color: b.color, avatarV: b.avatarV }}
              editing={b.editing}
              title={b.title}
            />
          ))}
          {badges.length > 3 ? <span className="avatar more">+{badges.length - 3}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** What `nodeView.ts` encodes for one presence badge — already a finished tooltip. */
interface BadgeRow {
  userId: string;
  initials: string;
  color: string;
  /** The account picture's version, null for none — see `components/Avatar.tsx`. */
  avatarV: number | null;
  editing: boolean;
  displayName: string;
  title: string;
}

/**
 * The colour every paint line derives from: the node's own, unless the clearance heatmap
 * (V0.39) has a bucket for it. Changing the INPUT and nothing else is the whole design — the
 * heat emits no class, so it composes with the four highlight layers automatically, and with
 * the mode off (`heat: ''`) the output is byte-identical to a build that never heard of heat.
 */
const paintOf = (v: NodeView): string => (v.heat ? (HEAT_HEX[v.heat as HeatBucket] ?? v.color) : v.color);

/**
 * The box's own paint.
 *
 * The root is a gradient with no border and is centred on its x (`translate(-50%,0)`); every
 * other node is tinted from its stored colour, differently per theme, and a left-side node is
 * laid out right-aligned by flipping the flex direction and pulling itself back by its own
 * width. All four of those are the original's, verbatim.
 */
function styleFor(v: NodeView): CSSProperties {
  const style: CSSProperties & Record<string, string | number | undefined> = {
    left: `${v.x}px`,
    top: `${v.y}px`,
  };
  const paint = paintOf(v);
  // The side flip is applied FIRST and to the root too, exactly as the original does: the
  // root's own block then overwrites `transform` and leaves `flexDirection` standing. It
  // never matters in practice (the root's side is 0 or 1) and it is copied rather than
  // simplified, because "never matters in practice" is not a thing a port gets to decide.
  if (v.side < 0) {
    style.flexDirection = 'row-reverse';
    style.transform = 'translateX(-100%)';
  }
  if (v.root) {
    // The root never takes heat — buildHeatIndex skips it — so its gradient stands.
    style.background = ROOT_GRADIENT;
    style.transform = 'translate(-50%,0)';
  } else {
    // The one formula (lib/color.ts) — the band's bubble, the dashboard chip, the search row
    // and the clone that flies between them all paint from it, so a node cannot change shade
    // as it crosses from one surface to another.
    Object.assign(style, nodeTint(paint, v.isLight));
  }
  if (v.hlColor) style['--hl'] = v.hlColor;
  if (v.dsColor) style['--dshl'] = STATUS_HL[v.dsColor] ?? '#2bb0d8';
  return style;
}

/**
 * The pipeline-stage highlight colour.
 *
 * A lookup rather than `PIPELINE_COLOR` directly, because the view model carries the stage
 * KEY (a primitive that shallow-compares) rather than the resolved custom property. The
 * values are `PIPELINE_DISPLAY`'s, spelled out so the shallow-compare stays over primitives.
 */
const STATUS_HL: Record<string, string> = {
  no_prompt: 'var(--status-not-started)',
  prompt_ready: 'var(--status-prompt-present)',
  draft: 'var(--review-draft)',
  generated: 'var(--status-doc-generated)',
  in_review: 'var(--review-in-review)',
  approved: 'var(--status-approved)',
  needs_update: 'var(--status-doc-rejected)',
};

/**
 * One documentation pill.
 *
 * Two shapes for one thing, and the difference is a zoom threshold: zoomed out it is a 19x10
 * blob whose only job is to say "there is documentation here, of this kind", zoomed in it is
 * a titled, clickable row. The markers (`data-opendoc-*`) are on both, so `MapStage`'s
 * delegation opens the page directly either way without going through the popup.
 */
function Bean({ bean, expanded }: { bean: BeanRow; expanded: boolean }) {
  const marker: Record<string, string> = bean.more
    ? { 'data-opendoc-more': '1' }
    : bean.planned
      ? // A PLANNED-but-missing pill (V0.42): its click CREATES the page, so it carries a
        // create marker rather than an open one. `kindPerm` says which kind to create.
        { 'data-createdoc-kind': bean.kindPerm ?? 'custom' }
      : bean.key
        ? { 'data-opendoc-kind': bean.key }
        : { 'data-opendoc-custom': bean.docId ?? '' };

  if (!expanded) {
    return (
      <span
        className="bean"
        {...marker}
        style={{
          background: bean.color,
          borderColor: `color-mix(in srgb,${bean.color} 70%, black)`,
        }}
        title={bean.label}
      />
    );
  }
  return (
    <DocPill
      variant="row"
      label={bean.label}
      color={bean.color}
      icon={bean.icon}
      planned={bean.planned}
      title={bean.planned ? `${bean.label} — planned, not yet created` : undefined}
      data={marker}
    />
  );
}

export const MapNode = memo(MapNodeInner);
